import React, { useState } from "react";
import { PanelContainer } from "@/components/chat/panels/PanelContainer";
import { BookOpenCheck } from "lucide-react";
import { PromptBox } from "@/components/content-chat/PromptBox";
import { GeneratingCard } from "@/components/content-chat/GeneratingCard";
import { QuizSettingsModal, QuizSettings } from "@/components/content-chat/modals";
import { QuizzesListSkeleton } from "@/components/content-chat/skeletons";
import { MyQuizzesList } from "@/components/quizzes/MyQuizzesList";
import { QuizTakingView } from "@/components/quizzes/QuizTakingView";
import { useContentContext } from "@/components/content-chat/ContentContext";
import { useContentTopicsFromChapters } from "@/hooks/useContentChapters";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getQuizzes, restartQuiz } from "@/features/quizzes/api";
import { getContentSourceText } from "@/lib/content/getContentSourceText";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { ContentType } from "@/types/content";
import type { Quiz } from "@/features/quizzes/types";

interface ContentQuizzesPanelProps {
  isMobile?: boolean;
  contentId?: string | null;
  onHelperRequest?: (req: import("@/types/quizHelpers").QuizHelperRequest) => void;
}

export const ContentQuizzesPanel: React.FC<ContentQuizzesPanelProps> = (props) => {
  const { user } = useAuth();
  const { content } = useContentContext();
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState<Quiz | null>(null);
  const { topics: availableTopics } = useContentTopicsFromChapters(
    content?.id,
    content?.type as ContentType | undefined
  );

  const handleOpenQuiz = (quiz: Quiz) => {
    setActiveQuiz(quiz);
  };

  const handleBackFromQuiz = () => {
    setActiveQuiz(null);
    // Refresh quiz list to show updated scores
    queryClient.invalidateQueries({ queryKey: ["quizzes", user?.id, props.contentId] });
  };

  const handleRestartQuiz = async () => {
    if (!activeQuiz) return;
    try {
      await restartQuiz(activeQuiz.id);
      queryClient.invalidateQueries({ queryKey: ["quizzes", user?.id, props.contentId] });
      toast({ title: "Quiz restarted" });
    } catch {
      toast({ title: "Failed to restart quiz", variant: "destructive" });
    }
  };

  const handleGenerateMore = () => {
    setActiveQuiz(null);
    // Open settings modal to generate more quizzes
    setSettingsOpen(true);
  };

  const { data: quizzes, isLoading } = useQuery({
    queryKey: ["quizzes", user?.id, props.contentId],
    queryFn: () => getQuizzes(user!.id, props.contentId),
    enabled: !!user,
  });

  const handleGenerate = async () => {
    if (!user || !content) return;

    setIsGenerating(true);
    try {
      // Fetch source text from content
      const sourceText = await getContentSourceText(content.id, content.type as ContentType);
      
      if (!sourceText) {
        toast({
          title: "Content not ready",
          description: "Please wait for the content to be processed before generating a quiz.",
          variant: "destructive",
        });
        return;
      }

      // Generate quiz with AI
      const { data, error } = await supabase.functions.invoke("create-quiz", {
        body: {
          user_id: user.id,
          content_id: content.id,
          title: `Quiz: ${content.title}`,
          question_count: 5,
          question_types: ["multiple_choice", "true_false"],
          difficulty: "medium",
          source_context: sourceText,
        },
      });

      if (error) throw error;

      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["quizzes", user.id, props.contentId] });
      
      toast({
        title: "Quiz generated!",
        description: `Created ${data.question_count} questions`,
      });
    } catch (error: any) {
      console.error("Error generating quiz:", error);
      
      toast({
        title: "Generation failed",
        description: error.message || "Failed to generate quiz. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSettings = () => {
    setSettingsOpen(true);
  };

  const handleSettingsSubmit = async (settings: QuizSettings) => {
    if (!user || !content) return;

    console.log("Quiz settings:", settings);
    setIsGenerating(true);
    try {
      // Fetch source text from content
      const sourceText = await getContentSourceText(content.id, content.type as ContentType);
      
      if (!sourceText) {
        toast({
          title: "Content not ready",
          description: "Please wait for the content to be processed before generating a quiz.",
          variant: "destructive",
        });
        return;
      }

      // Map question types to the format expected by the edge function
      const questionTypeMap: Record<string, string> = {
        'Multiple Choice': 'multiple_choice',
        'True or False': 'true_false',
        'Free Response': 'free_response',
        'Fill in the blank': 'fill_blank',
      };
      
      const mappedTypes = (settings.questionTypes || ['Multiple Choice', 'True or False'])
        .map(t => questionTypeMap[t] || 'multiple_choice');

      // Generate quiz with custom settings
      const { data, error } = await supabase.functions.invoke("create-quiz", {
        body: {
          user_id: user.id,
          content_id: content.id,
          title: `Quiz: ${content.title}`,
          question_count: settings.questionCount || 5,
          question_types: mappedTypes,
          difficulty: settings.difficulty?.toLowerCase() || "medium",
          topics: settings.topics || [],
          instructions: settings.instructions || null,
          source_context: sourceText,
        },
      });

      if (error) throw error;

      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["quizzes", user.id, props.contentId] });
      
      toast({
        title: "Quiz generated!",
        description: `Created ${data.question_count} questions`,
      });
    } catch (error: any) {
      console.error("Error generating quiz:", error);
      
      toast({
        title: "Generation failed",
        description: error.message || "Failed to generate quiz. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  if (!user) {
    return (
      <PanelContainer
        {...props}
        title="Quizzes"
        description={
          <>
            Sign in to create and take quizzes. Ask Shattara AI:{" "}
            <span className="font-bold">"Quiz me on Newton's laws with 5 questions."</span>
          </>
        }
        icon={<BookOpenCheck className="h-4 w-4 text-primary" />}
      >
        <div className="p-3 text-xs text-muted-foreground">
          Please log in to use quizzes.
        </div>
      </PanelContainer>
    );
  }

  // Check if quizzes exist
  const hasQuizzes = !isLoading && quizzes && quizzes.length > 0;

  // If a quiz is active, show the QuizTakingView
  if (activeQuiz) {
    return (
      <QuizTakingView
        quiz={activeQuiz}
        onBack={handleBackFromQuiz}
        onRestart={handleRestartQuiz}
        onGenerateMore={handleGenerateMore}
        onHelperRequest={props.onHelperRequest}
      />
    );
  }

  return (
    <>
      <PanelContainer
        {...props}
        title="Quizzes"
        description={
          <>
            Try asking Shattara AI:{" "}
            <span className="font-bold">"Quiz me on Newton's laws with 5 questions."</span>
          </>
        }
        icon={<BookOpenCheck className="h-4 w-4 text-primary" />}
      >
        <div className="flex flex-col">
          {/* Always show PromptBox or GeneratingCard */}
          {isGenerating ? (
            <GeneratingCard type="quiz" />
          ) : (
            <PromptBox
              type="quiz"
              onGenerate={handleGenerate}
              onSettings={handleSettings}
              isGenerating={isGenerating}
            />
          )}
          
          {/* Show loading skeleton while fetching */}
          {isLoading && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">My Quizzes</h3>
              <QuizzesListSkeleton count={3} />
            </div>
          )}
          
          {/* Show quiz list below when quizzes exist */}
          {!isLoading && hasQuizzes && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">My Quizzes</h3>
              <MyQuizzesList
                userId={user.id}
                contentId={props.contentId ?? null}
                onOpenQuiz={handleOpenQuiz}
              />
            </div>
          )}
        </div>
      </PanelContainer>
      
      <QuizSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSubmit={handleSettingsSubmit}
        availableTopics={availableTopics}
      />
    </>
  );
};
